import { useState, useEffect } from "react";
import { PERSONAS, ALL_PERSONA_IDS, DEFAULT_COURT } from "./personas";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function fetchPerspective(situation, persona_id) {
  const res = await fetch(`${API_BASE}/api/perspective`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ situation, persona_id }),
  });
  return res.json();
}

async function selectPersonas(situation) {
  const res = await fetch(`${API_BASE}/api/select-personas`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ situation }),
  });
  const data = await res.json();
  return data.personas || DEFAULT_COURT;
}

async function checkPrivacy(situation) {
  const res = await fetch(`${API_BASE}/api/privacy-check`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ situation }),
  });
  return res.json();
}

async function shareSession(situation, perspectives, courtPersonas, acceptance) {
  const res = await fetch(`${API_BASE}/api/sessions`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ situation, perspectives, courtPersonas, acceptance }),
  });
  return res.json();
}

async function fetchPublicSessions() {
  const res = await fetch(`${API_BASE}/api/sessions`);
  const data = await res.json();
  return data.sessions || [];
}

async function acceptPublicSession(sessionId) {
  const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/accept`, {
    method: "POST", headers: { "Content-Type": "application/json" },
  });
  return res.json();
}

function loadHistory() {
  try { return JSON.parse(localStorage.getItem("byr_history") || "[]"); }
  catch { return []; }
}

function persistSession(situation, perspectives, courtPersonas, acceptedPersonaId, acceptance) {
  try {
    const prev = loadHistory();
    const session = {
      id: Date.now().toString(), situation,
      timestamp: new Date().toISOString(),
      perspectives, courtPersonas,
      acceptance: acceptance || { original: acceptedPersonaId, reviews: 0 },
    };
    const updated = [session, ...prev].slice(0, 20);
    localStorage.setItem("byr_history", JSON.stringify(updated));
    return updated;
  } catch { return loadHistory(); }
}

function recordReview(sessionId) {
  try {
    const history = loadHistory();
    const updated = history.map(s => s.id !== sessionId ? s : {
      ...s,
      acceptance: { ...(s.acceptance || { original: null, reviews: 0 }), reviews: ((s.acceptance || {}).reviews || 0) + 1 },
    });
    localStorage.setItem("byr_history", JSON.stringify(updated));
    return updated;
  } catch { return loadHistory(); }
}

function verdictInfo(verdict) {
  if (verdict === "valid")     return { label: "Justified", color: "#2d6a4f" };
  if (verdict === "let_it_go") return { label: "Let It Go", color: "#b03a2a" };
  return                              { label: "Nuanced",   color: "#8b5e00" };
}

function echoLabel(verdict) {
  if (verdict === "valid")     return { text: "SOUGHT VALIDATION", color: "#7a1d1d", bg: "#fdf0f0" };
  if (verdict === "let_it_go") return { text: "OPEN TO CHALLENGE", color: "#2d6a4f", bg: "#eef5ef" };
  return                              { text: "GENUINELY DEBATABLE", color: "#8b5e00", bg: "#fdf5e4" };
}

// ── Slider ───────────────────────────────────────────────────────────────────

function PerspectiveSlider({ perspectives, courtPersonas, selected, onSelect }) {
  const entries = Object.entries(perspectives);
  if (entries.length === 0) return null;

  return (
    <div style={{ background: "white", border: "1px solid #e8ddd0", borderRadius: 12, padding: "14px 16px 12px", marginBottom: 14 }}>
      <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b4a48a", letterSpacing: "0.15em", marginBottom: 10 }}>
        TAP A VOICE TO READ THEIR RULING
      </div>
      <div style={{ position: "relative", height: 72 }}>
        <div style={{
          position: "absolute", top: "50%", left: 14, right: 14,
          height: 5, borderRadius: 3, transform: "translateY(-50%)",
          background: "linear-gradient(90deg, #2d6a4f, #d4a017 50%, #b03a2a)",
        }} />
        {entries.map(([personaId, data]) => {
          const p = PERSONAS[personaId];
          const isSelected = selected === personaId;
          const idx = courtPersonas.indexOf(personaId);
          const above = idx % 2 === 0;
          const left = `calc(14px + ${data.score}% * (100% - 28px) / 100%)`;
          return (
            <button
              key={personaId}
              onClick={() => onSelect(isSelected ? null : personaId)}
              title={p.name}
              style={{
                position: "absolute", left, transform: "translateX(-50%)",
                top: above ? 2 : "auto", bottom: above ? "auto" : 2,
                width: 32, height: 32, borderRadius: "50%", padding: 0,
                border: isSelected ? `2px solid ${p.accent}` : "2px solid #e8ddd0",
                background: isSelected ? p.bg : "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, cursor: "pointer",
                zIndex: isSelected ? 10 : 5,
                boxShadow: isSelected ? `0 2px 8px ${p.border}` : "0 1px 4px rgba(0,0,0,0.15)",
                transition: "all 0.15s",
              }}
            >{p.emoji}</button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#2d6a4f", letterSpacing: "0.1em" }}>JUSTIFIED</span>
        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b03a2a", letterSpacing: "0.1em" }}>LET IT GO</span>
      </div>
    </div>
  );
}

// ── Perspective Card ──────────────────────────────────────────────────────────

function PerspectiveCard({ personaId, data, onAccept, compact = false, highlighted = false }) {
  const p = PERSONAS[personaId];
  const { label, color } = verdictInfo(data.verdict);

  if (compact) {
    return (
      <div style={{
        flex: 1, minWidth: 0,
        background: highlighted ? p.bg : "white",
        border: `1.5px solid ${highlighted ? p.border : "#e8ddd0"}`,
        borderRadius: 12, padding: "12px 10px", transition: "all 0.2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 18 }}>{p.emoji}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 12, fontWeight: 700, color: p.accent, lineHeight: 1.2 }}>{p.name}</div>
            <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: "0.04em" }}>{label.toUpperCase()}</div>
          </div>
        </div>
        <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#2c1f0e", margin: "0 0 10px", lineHeight: 1.55 }}>
          {data.response}
        </p>
        <button onClick={() => onAccept(personaId)} style={{
          width: "100%", padding: "10px 0",
          background: "#2c1f0e", color: "#f5efe6", border: "none",
          borderRadius: 8, fontFamily: "'Courier Prime', monospace",
          fontSize: 11, letterSpacing: "0.1em", cursor: "pointer",
        }}>ACCEPT</button>
      </div>
    );
  }

  return (
    <div style={{
      background: p.bg, border: `1.5px solid ${p.border}`,
      borderRadius: 14, padding: "18px 16px", marginBottom: 14,
      animation: "fadeIn 0.3s ease both",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
          background: "white", border: `2px solid ${p.border}`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
        }}>{p.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: p.accent }}>{p.name}</div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 11, color: "#9a8a6a" }}>{p.title}</div>
        </div>
        <div style={{
          background: color + "20", border: `1px solid ${color}50`,
          borderRadius: 20, padding: "3px 10px", flexShrink: 0,
          fontFamily: "'Courier Prime', monospace", fontSize: 10, color, fontWeight: 700, letterSpacing: "0.06em",
        }}>{label}</div>
      </div>

      <div style={{ background: "white", borderRadius: 8, padding: "10px 12px", marginBottom: 12, border: `1px solid ${p.border}50` }}>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 8, color: "#b4a48a", letterSpacing: "0.12em", marginBottom: 4 }}>THEIR WORLDVIEW</div>
        <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#6b5a3e", margin: 0, lineHeight: 1.55 }}>{p.about}</p>
      </div>

      <p style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#2c1f0e", margin: "0 0 12px", lineHeight: 1.7 }}>
        {data.response}
      </p>

      {data.key_tension && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14,
          background: "white", border: `1px solid ${p.border}`, borderRadius: 20,
          padding: "5px 12px", fontFamily: "Georgia, serif", fontSize: 12, color: p.accent,
        }}>◆ {data.key_tension}</div>
      )}

      <button onClick={() => onAccept(personaId)} style={{
        width: "100%", padding: "14px 0",
        background: "#2c1f0e", color: "#f5efe6", border: "none",
        borderRadius: 10, fontFamily: "'Playfair Display', serif",
        fontSize: 16, fontWeight: 700, cursor: "pointer",
      }}>Accept This Ruling</button>
    </div>
  );
}

// ── Share Dialog ──────────────────────────────────────────────────────────────

function ShareDialog({ situation, acceptance, perspectives, courtPersonas, onClose }) {
  const [state, setState] = useState("prompt");
  const [warning, setWarning] = useState(null);

  const doShare = async () => {
    setState("sharing");
    try {
      await shareSession(situation, perspectives, courtPersonas, acceptance);
    } catch {}
    setState("done");
    setTimeout(onClose, 1500);
  };

  const handleShare = async () => {
    setState("checking");
    try {
      const check = await checkPrivacy(situation);
      if (!check.safe) {
        setWarning(check.warning || "This situation may contain identifying details.");
        setState("warning");
        return;
      }
    } catch {}
    await doShare();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(44,31,14,0.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
      <div style={{ background: "#faf7f2", borderRadius: "20px 20px 0 0", padding: "24px 18px 44px", width: "100%", maxWidth: 480, margin: "0 auto" }}>
        {state === "prompt" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#2c1f0e", marginBottom: 8 }}>Ruling saved.</div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#6b5a3e", margin: "0 0 20px", lineHeight: 1.6 }}>
              Share this session anonymously with the community? No names are attached — just the situation and ruling.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "12px 0", background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e", borderRadius: 8, fontFamily: "'Courier Prime', monospace", fontSize: 11, cursor: "pointer", letterSpacing: "0.08em" }}>KEEP PRIVATE</button>
              <button onClick={handleShare} style={{ flex: 1, padding: "12px 0", background: "#2c1f0e", color: "#f5efe6", border: "none", borderRadius: 8, fontFamily: "'Courier Prime', monospace", fontSize: 11, cursor: "pointer", letterSpacing: "0.08em" }}>SHARE ANONYMOUSLY</button>
            </div>
          </>
        )}
        {(state === "checking" || state === "sharing") && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ width: 20, height: 20, border: "2px solid #d4c4a8", borderTopColor: "#8b5e3c", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, color: "#b4a48a", letterSpacing: "0.1em" }}>
              {state === "checking" ? "REVIEWING FOR PRIVACY…" : "SHARING…"}
            </div>
          </div>
        )}
        {state === "warning" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: "#b03a2a", marginBottom: 8 }}>Privacy Notice</div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#6b5a3e", margin: "0 0 16px", lineHeight: 1.6 }}>
              {warning}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: "12px 0", background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e", borderRadius: 8, fontFamily: "'Courier Prime', monospace", fontSize: 11, cursor: "pointer", letterSpacing: "0.08em" }}>KEEP PRIVATE</button>
              <button onClick={doShare} style={{ flex: 1, padding: "12px 0", background: "#8b5e3c", color: "#f5efe6", border: "none", borderRadius: 8, fontFamily: "'Courier Prime', monospace", fontSize: 11, cursor: "pointer", letterSpacing: "0.08em" }}>SHARE ANYWAY</button>
            </div>
          </>
        )}
        {state === "done" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: "#2d6a4f" }}>Shared with the community.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Swap Court Modal ──────────────────────────────────────────────────────────

function SwapModal({ courtPersonas, onApply, onClose }) {
  const [pending, setPending] = useState(new Set(courtPersonas));

  const toggle = (id) => {
    setPending(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else if (next.size < 5) { next.add(id); }
      return next;
    });
  };

  const changed = ALL_PERSONA_IDS.some(id => pending.has(id) !== courtPersonas.includes(id));
  const ready = pending.size === 5;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(44,31,14,0.5)", zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#faf7f2", borderRadius: "20px 20px 0 0", padding: "20px 16px 40px", maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#2c1f0e" }}>Customize the Court</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 26, cursor: "pointer", color: "#9a8a6a", padding: 0, lineHeight: 1 }}>×</button>
        </div>
        <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#9a8a6a", margin: "0 0 16px" }}>
          {pending.size}/5 selected · tap to add or remove
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
          {ALL_PERSONA_IDS.map(id => {
            const p = PERSONAS[id];
            const isIn = pending.has(id);
            const isOriginal = courtPersonas.includes(id);
            return (
              <button key={id} onClick={() => toggle(id)} style={{
                textAlign: "left", padding: "10px 12px",
                background: isIn ? p.bg : "white",
                border: `1.5px solid ${isIn ? p.border : "#e8ddd0"}`,
                borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                opacity: !isIn && pending.size >= 5 ? 0.4 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 16 }}>{p.emoji}</span>
                  {isIn && <span style={{ marginLeft: "auto", color: p.accent, fontSize: 12, fontWeight: 700 }}>✓</span>}
                  {!isIn && isOriginal && <span style={{ marginLeft: "auto", fontSize: 9, color: "#b4a48a", fontFamily: "'Courier Prime', monospace" }}>WAS IN</span>}
                </div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 12, fontWeight: 700, color: p.accent, lineHeight: 1.2 }}>{p.name}</div>
                <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b4a48a", letterSpacing: "0.04em" }}>{p.title}</div>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => ready && changed && onApply([...pending])}
          disabled={!ready || !changed}
          style={{
            width: "100%", padding: "14px 0",
            background: ready && changed ? "#2c1f0e" : "#e8ddd0",
            color: ready && changed ? "#f5efe6" : "#b4a48a",
            border: "none", borderRadius: 10,
            fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700,
            cursor: ready && changed ? "pointer" : "not-allowed",
          }}
        >
          {!ready ? `Select ${5 - pending.size} more` : !changed ? "No changes" : "Reconvene the Court"}
        </button>
      </div>
    </div>
  );
}

// ── History Modal ─────────────────────────────────────────────────────────────

function HistoryModal({ history, onRestore, onClose, publicSessions, onLoadPublic, loadingPublic, onAcceptPublic }) {
  const [tab, setTab] = useState("mine");

  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === "community") onLoadPublic();
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(44,31,14,0.5)", zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#faf7f2", borderRadius: "20px 20px 0 0", padding: "0 0 40px", maxHeight: "80vh", overflowY: "auto" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 18px 0" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#2c1f0e" }}>Sessions</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 28, cursor: "pointer", color: "#9a8a6a", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #e8ddd0", margin: "12px 0 0" }}>
          {[["mine", "YOUR SESSIONS"], ["community", "COMMUNITY"]].map(([key, label]) => (
            <button key={key} onClick={() => handleTabChange(key)} style={{
              flex: 1, padding: "10px 0", background: "none", border: "none",
              fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: "0.1em",
              cursor: "pointer", color: tab === key ? "#2c1f0e" : "#b4a48a",
              borderBottom: tab === key ? "2px solid #2c1f0e" : "2px solid transparent",
            }}>{label}</button>
          ))}
        </div>

        <div style={{ padding: "16px 18px 0" }}>
          {tab === "mine" && (
            history.length === 0 ? (
              <p style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#9a8a6a", textAlign: "center", padding: "20px 0" }}>
                No saved sessions yet. Accept a ruling to save your first session.
              </p>
            ) : history.map(session => {
              const scores = Object.values(session.perspectives || {}).map(p => p.score).filter(s => s != null);
              const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
              const d = new Date(session.timestamp);
              const acc = session.acceptance || {};
              const acceptedPersona = acc.original ? PERSONAS[acc.original] : null;
              const reviewCount = acc.reviews || 0;
              const echo = acc.verdict ? echoLabel(acc.verdict) : null;

              return (
                <button key={session.id} onClick={() => onRestore(session)} style={{
                  width: "100%", textAlign: "left", display: "block",
                  background: "white", border: "1px solid #e8ddd0",
                  borderRadius: 12, padding: "14px 16px", marginBottom: 10, cursor: "pointer",
                }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#2c1f0e", margin: "0 0 8px", lineHeight: 1.5 }}>
                    "{session.situation.length > 90 ? session.situation.slice(0, 90) + "…" : session.situation}"
                  </p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                    <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: "#b4a48a" }}>
                      {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </span>
                    {avg != null && (
                      <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, fontWeight: 700, color: avg < 40 ? "#2d6a4f" : avg > 60 ? "#b03a2a" : "#8b5e00" }}>
                        avg {avg}/100
                      </span>
                    )}
                  </div>
                  {(acceptedPersona || reviewCount > 0 || echo) && (
                    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {acceptedPersona && (
                        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, background: "#eef5ef", border: "1px solid #2d6a4f40", borderRadius: 10, padding: "2px 8px", color: "#2d6a4f", letterSpacing: "0.06em" }}>
                          {acceptedPersona.emoji} Accepted: {acceptedPersona.name}
                        </span>
                      )}
                      {echo && (
                        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, background: echo.bg, border: `1px solid ${echo.color}40`, borderRadius: 10, padding: "2px 8px", color: echo.color, letterSpacing: "0.06em" }}>
                          {echo.text}
                        </span>
                      )}
                      {reviewCount > 0 && (
                        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, background: "#f3f0fd", border: "1px solid #a78bfa40", borderRadius: 10, padding: "2px 8px", color: "#4c1d95", letterSpacing: "0.06em" }}>
                          +{reviewCount} reviewer{reviewCount !== 1 ? "s" : ""} agreed
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })
          )}

          {tab === "community" && (
            loadingPublic ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <div style={{ width: 20, height: 20, border: "2px solid #d4c4a8", borderTopColor: "#8b5e3c", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
              </div>
            ) : publicSessions.length === 0 ? (
              <p style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#9a8a6a", textAlign: "center", padding: "20px 0" }}>
                No shared sessions yet. Be the first to share after accepting a ruling.
              </p>
            ) : [...publicSessions].reverse().map(session => {
              const acc = session.acceptance || {};
              const acceptedPersona = acc.original ? PERSONAS[acc.original] : null;
              const echo = acc.verdict ? echoLabel(acc.verdict) : null;
              const d = new Date(session.ts);
              const reviewCount = acc.reviews || 0;

              return (
                <div key={session.id} style={{ background: "white", border: "1px solid #e8ddd0", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#2c1f0e", margin: "0 0 8px", lineHeight: 1.5 }}>
                    "{session.situation.length > 90 ? session.situation.slice(0, 90) + "…" : session.situation}"
                  </p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                    <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: "#b4a48a" }}>
                      {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                    {echo && (
                      <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, background: echo.bg, border: `1px solid ${echo.color}40`, borderRadius: 10, padding: "2px 8px", color: echo.color, letterSpacing: "0.06em" }}>
                        {echo.text}
                      </span>
                    )}
                  </div>
                  {acceptedPersona && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                      <span style={{ fontSize: 14 }}>{acceptedPersona.emoji}</span>
                      <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#9a8a6a" }}>Accepted {acceptedPersona.name}</span>
                      {reviewCount > 0 && (
                        <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b4a48a", marginLeft: "auto" }}>
                          +{reviewCount} agreed
                        </span>
                      )}
                    </div>
                  )}
                  <button onClick={() => onAcceptPublic(session.id)} style={{
                    width: "100%", padding: "8px 0",
                    background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e",
                    borderRadius: 8, fontFamily: "'Courier Prime', monospace",
                    fontSize: 10, cursor: "pointer", letterSpacing: "0.08em",
                  }}>I AGREE WITH THIS RULING</button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [situation, setSituation]           = useState("");
  const [submitted, setSubmitted]           = useState("");
  const [courtPersonas, setCourtPersonas]   = useState(DEFAULT_COURT);
  const [courtSessionId, setCourtSessionId] = useState(0);
  const [selecting, setSelecting]           = useState(false);
  const [phase, setPhase]                   = useState("input");
  const [perspectives, setPerspectives]     = useState({});
  const [selected, setSelected]             = useState(null);
  const [showHistory, setShowHistory]       = useState(false);
  const [showSwap, setShowSwap]             = useState(false);
  const [history, setHistory]               = useState(loadHistory);
  const [sessionRef, setSessionRef]         = useState(null);
  const [expandedPersona, setExpandedPersona] = useState(null);
  const [sharePrompt, setSharePrompt]       = useState(null);
  const [publicSessions, setPublicSessions] = useState([]);
  const [loadingPublic, setLoadingPublic]   = useState(false);

  // Fetch all court personas when court is ready
  useEffect(() => {
    if (!submitted || courtPersonas.length === 0) return;
    const timeouts = courtPersonas.map((personaId, i) =>
      setTimeout(async () => {
        try {
          const result = await fetchPerspective(submitted, personaId);
          setPerspectives(prev => ({ ...prev, [personaId]: result }));
        } catch {}
      }, i * 300)
    );
    return () => timeouts.forEach(clearTimeout);
  }, [courtSessionId]); // eslint-disable-line

  const handleSubmit = async () => {
    if (!situation.trim() || selecting) return;
    const text = situation.trim();
    setSubmitted(text);
    setPerspectives({});
    setSelected(null);
    setSessionRef(null);
    setSharePrompt(null);
    setSelecting(true);
    setPhase("results");
    try {
      const personas = await selectPersonas(text);
      setCourtPersonas(personas);
    } catch {
      setCourtPersonas(DEFAULT_COURT);
    }
    setSelecting(false);
    setCourtSessionId(n => n + 1);
  };

  const handleNew = () => {
    setSituation(""); setSubmitted(""); setPerspectives({});
    setSelected(null); setSessionRef(null); setSharePrompt(null); setPhase("input");
  };

  const handleReEdit = () => {
    setSituation(submitted);
    setPhase("input");
  };

  const handleAccept = (personaId) => {
    const acceptedVerd = perspectives[personaId]?.verdict || "borderline";
    if (sessionRef) {
      const updated = recordReview(sessionRef);
      setHistory(updated);
      handleNew();
    } else {
      const acceptance = { original: personaId, verdict: acceptedVerd, reviews: 0 };
      const updated = persistSession(submitted, perspectives, courtPersonas, personaId, acceptance);
      setHistory(updated);
      setSharePrompt({ acceptance });
    }
  };

  const handleShareClose = () => {
    setSharePrompt(null);
    handleNew();
  };

  const handleRestore = (session) => {
    setSubmitted(session.situation);
    setSituation(session.situation);
    setPerspectives(session.perspectives);
    setCourtPersonas(session.courtPersonas || DEFAULT_COURT);
    setSelected(null);
    setSessionRef(session.id);
    setPhase("results");
    setShowHistory(false);
  };

  const handleApplyCourt = (newCourt) => {
    const removed = courtPersonas.filter(id => !newCourt.includes(id));
    const added   = newCourt.filter(id => !courtPersonas.includes(id));

    if (selected && removed.includes(selected)) setSelected(null);
    setPerspectives(prev => {
      const next = { ...prev };
      removed.forEach(id => delete next[id]);
      return next;
    });
    setCourtPersonas(newCourt);
    setShowSwap(false);

    added.forEach(async (personaId) => {
      try {
        const result = await fetchPerspective(submitted, personaId);
        setPerspectives(prev => ({ ...prev, [personaId]: result }));
      } catch {}
    });
  };

  const handleLoadPublic = async () => {
    setLoadingPublic(true);
    try {
      const sessions = await fetchPublicSessions();
      setPublicSessions(sessions);
    } catch {}
    setLoadingPublic(false);
  };

  const handleAcceptPublic = async (sessionId) => {
    try {
      await acceptPublicSession(sessionId);
      setPublicSessions(prev => prev.map(s => s.id !== sessionId ? s : {
        ...s,
        acceptance: { ...s.acceptance, reviews: (s.acceptance?.reviews || 0) + 1 },
      }));
    } catch {}
  };

  // Derived
  const loaded   = Object.entries(perspectives);
  const sorted   = [...loaded].sort((a, b) => a[1].score - b[1].score);
  const mostJust = sorted[0];
  const mostLet  = sorted[sorted.length - 1];
  const loadedN  = loaded.length;

  const scores = loaded.map(([, d]) => d.score);
  const avg    = scores.length >= 2 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const spread = scores.length >= 2 ? Math.max(...scores) - Math.min(...scores) : null;

  const consensusLabel = avg == null ? null
    : avg < 35 ? { text: "BROADLY JUSTIFIED",  color: "#2d6a4f", bg: "#eef5ef" }
    : avg > 65 ? { text: "LET IT GO",          color: "#b03a2a", bg: "#fdf0ee" }
    :            { text: "GENUINELY DEBATABLE", color: "#8b5e00", bg: "#fdf5e4" };

  const showFull = selected && perspectives[selected];

  return (
    <div style={{ minHeight: "100vh", background: "#faf7f2", maxWidth: 480, margin: "0 auto" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Courier+Prime:wght@400;700&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        textarea:focus { outline: none; }
        textarea::placeholder { color: #c4b49a; font-family: Georgia, serif; }
        button { -webkit-appearance: none; }
      `}</style>

      <div style={{ height: 4, background: "linear-gradient(90deg, #8b5e3c, #d4a017, #2d6a4f, #2d5a8e, #5a3e6b)" }} />

      {/* ── Header ── */}
      <div style={{
        background: "white", borderBottom: "1px solid #e8ddd0", position: "relative",
        padding: phase === "results" ? "16px 16px 16px" : "36px 16px 28px",
        textAlign: "center",
      }}>
        <button onClick={() => setShowHistory(true)} aria-label="History" style={{
          position: "absolute", right: 14, top: phase === "results" ? 14 : 36,
          background: "none", border: "1px solid #e8ddd0", borderRadius: 8,
          padding: "7px 9px", cursor: "pointer", color: "#9a8a6a", fontSize: 16, lineHeight: 1,
        }}>🕐</button>

        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b4a48a", letterSpacing: "0.22em", marginBottom: 8 }}>◆ THE COURT OF PERSPECTIVES ◆</div>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontWeight: 900, color: "#2c1f0e",
          margin: "0 0 6px", lineHeight: 1, letterSpacing: "-0.02em",
          fontSize: phase === "results" ? "1.75rem" : "clamp(2.2rem, 9vw, 3.5rem)",
        }}>Before You React</h1>

        {phase === "input" && (
          <>
            <div style={{ width: 44, height: 2, background: "linear-gradient(90deg,#d4a017,#8b5e3c)", margin: "10px auto 22px", borderRadius: 1 }} />
            <div style={{ maxWidth: 440, margin: "0 auto", textAlign: "left" }}>
              <textarea
                value={situation}
                onChange={e => setSituation(e.target.value)}
                placeholder="Describe the situation that has you upset, frustrated, or ready to escalate…"
                rows={4} maxLength={1000}
                style={{
                  width: "100%", background: "#faf7f2", border: "1px solid #d4c4a8",
                  borderRadius: 10, padding: "14px 16px",
                  fontFamily: "Georgia, serif", fontSize: 16, color: "#2c1f0e",
                  resize: "none", lineHeight: 1.6, marginBottom: 12, transition: "border-color 0.2s",
                }}
                onFocus={e => { e.target.style.borderColor = "#8b5e3c"; e.target.style.background = "white"; }}
                onBlur={e => { e.target.style.borderColor = "#d4c4a8"; e.target.style.background = "#faf7f2"; }}
                onKeyDown={e => e.key === "Enter" && e.metaKey && handleSubmit()}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: "#c4b49a" }}>{situation.length}/1000</span>
                <button onClick={handleSubmit} disabled={!situation.trim() || selecting} style={{
                  background: situation.trim() && !selecting ? "#2c1f0e" : "#e8ddd0",
                  color: situation.trim() && !selecting ? "#f5efe6" : "#b4a48a",
                  border: "none", borderRadius: 10, padding: "13px 24px",
                  fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700,
                  cursor: situation.trim() && !selecting ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  {selecting ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(245,239,230,0.3)", borderTopColor: "#f5efe6", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />Selecting…</> : "Convene the Court"}
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "results" && (
          <div style={{ marginTop: 10, textAlign: "left" }}>
            <div style={{ background: "#faf7f2", border: "1px solid #e8ddd0", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
              <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 8, color: "#b4a48a", letterSpacing: "0.12em", marginBottom: 2 }}>UNDER REVIEW</div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#4a3e2e", margin: 0, lineHeight: 1.5 }}>"{submitted}"</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleReEdit} style={{
                flex: 1, padding: "10px 0",
                background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e",
                borderRadius: 8, fontFamily: "'Courier Prime', monospace",
                fontSize: 11, cursor: "pointer", letterSpacing: "0.08em",
              }}>RE-EDIT</button>
              <button onClick={handleNew} style={{
                flex: 1, padding: "10px 0",
                background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e",
                borderRadius: 8, fontFamily: "'Courier Prime', monospace",
                fontSize: 11, cursor: "pointer", letterSpacing: "0.08em",
              }}>NEW</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Results ── */}
      {phase === "results" && (
        <div style={{ padding: "16px 14px 60px" }}>

          {/* Selecting / loading */}
          {(selecting || loadedN < courtPersonas.length) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 14, height: 14, border: "2px solid #d4c4a8", borderTopColor: "#8b5e3c", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
              <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, color: "#b4a48a", letterSpacing: "0.12em" }}>
                {selecting ? "SELECTING VOICES…" : `DELIBERATING… ${loadedN}/${courtPersonas.length}`}
              </span>
            </div>
          )}

          {/* Consensus */}
          {consensusLabel && (
            <div style={{ background: consensusLabel.bg, border: `1px solid ${consensusLabel.color}30`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 8, color: "#9a8a6a", letterSpacing: "0.15em", marginBottom: 4 }}>THE COURT FINDS</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: consensusLabel.color, marginBottom: 10 }}>{consensusLabel.text}</div>
              <div style={{ height: 5, background: "#e8ddd0", borderRadius: 3, overflow: "hidden", border: "1px solid #d4c4a8" }}>
                <div style={{ height: "100%", width: `${avg}%`, background: "linear-gradient(90deg,#2d6a4f,#d4a017 50%,#b03a2a)", transition: "width 1.5s ease", borderRadius: 3 }} />
              </div>
              {spread != null && (
                <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#9a8a6a", marginTop: 5, letterSpacing: "0.08em" }}>
                  avg {avg}/100 · {spread > 40 ? "divided court" : spread > 20 ? "moderate debate" : "strong consensus"}
                </div>
              )}
            </div>
          )}

          {/* Slider */}
          <PerspectiveSlider
            perspectives={perspectives}
            courtPersonas={courtPersonas}
            selected={selected}
            onSelect={setSelected}
          />

          {/* Swap court button */}
          {!selecting && (
            <button onClick={() => setShowSwap(true)} style={{
              width: "100%", padding: "10px 0", marginBottom: 14,
              background: "white", border: "1px solid #d4c4a8", color: "#6b5a3e",
              borderRadius: 8, fontFamily: "'Courier Prime', monospace",
              fontSize: 10, cursor: "pointer", letterSpacing: "0.1em",
            }}>
              CUSTOMIZE THE COURT · {courtPersonas.length} OF {ALL_PERSONA_IDS.length} VOICES
            </button>
          )}

          {/* Full card for selected */}
          {showFull && (
            <PerspectiveCard
              personaId={selected}
              data={perspectives[selected]}
              onAccept={handleAccept}
            />
          )}

          {/* Extremes */}
          {mostJust && mostLet && (
            <div style={{ display: "flex", gap: 10 }}>
              <PerspectiveCard personaId={mostJust[0]} data={mostJust[1]} onAccept={handleAccept} compact highlighted={selected === mostJust[0]} />
              {mostJust[0] !== mostLet[0] && (
                <PerspectiveCard personaId={mostLet[0]} data={mostLet[1]} onAccept={handleAccept} compact highlighted={selected === mostLet[0]} />
              )}
            </div>
          )}

          {loadedN === courtPersonas.length && loadedN > 0 && (
            <p style={{ textAlign: "center", fontFamily: "Georgia, serif", fontSize: 13, color: "#c4b49a", lineHeight: 1.7, paddingTop: 20 }}>
              The disagreement between perspectives is the point.<br />No single lens holds the full truth.
            </p>
          )}
        </div>
      )}

      {/* ── Input: persona grid ── */}
      {phase === "input" && (
        <div style={{ padding: "20px 14px 60px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {ALL_PERSONA_IDS.map((id, i) => {
            const p = PERSONAS[id];
            const isExpanded = expandedPersona === id;
            return (
              <div
                key={id}
                onClick={() => setExpandedPersona(isExpanded ? null : id)}
                style={{
                  background: isExpanded ? p.bg : "white",
                  border: `1px solid ${isExpanded ? p.border : p.border + "60"}`,
                  borderRadius: 10, padding: 14, cursor: "pointer",
                  animation: `fadeIn 0.4s ease ${i * 0.04}s both`,
                  transition: "all 0.2s",
                  gridColumn: isExpanded ? "1 / -1" : undefined,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{p.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 700, color: p.accent, lineHeight: 1.2 }}>{p.name}</div>
                    <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#b4a48a", letterSpacing: "0.05em" }}>{p.title}</div>
                  </div>
                  <span style={{ color: "#c4b49a", fontSize: 10, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>▼</span>
                </div>
                {isExpanded && (
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#4a3e2e", margin: "12px 0 0", lineHeight: 1.65 }}>
                    {p.about}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ borderTop: "1px solid #e8ddd0", background: "white", padding: "18px", textAlign: "center" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 13, fontWeight: 700, color: "#2c1f0e", marginBottom: 2 }}>Before You React</div>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 9, color: "#c4b49a", letterSpacing: "0.1em" }}>THE COURT OF PERSPECTIVES · EARLY ACCESS</div>
      </div>

      {showSwap && <SwapModal courtPersonas={courtPersonas} onApply={handleApplyCourt} onClose={() => setShowSwap(false)} />}
      {showHistory && (
        <HistoryModal
          history={history}
          onRestore={handleRestore}
          onClose={() => setShowHistory(false)}
          publicSessions={publicSessions}
          onLoadPublic={handleLoadPublic}
          loadingPublic={loadingPublic}
          onAcceptPublic={handleAcceptPublic}
        />
      )}
      {sharePrompt && (
        <ShareDialog
          situation={submitted}
          acceptance={sharePrompt.acceptance}
          perspectives={perspectives}
          courtPersonas={courtPersonas}
          onClose={handleShareClose}
        />
      )}
    </div>
  );
}
